import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Layout } from '@/components/layout';
import { Card, Button, EmptyState, ErrorState, Spinner, Badge } from '@/components/atoms';
import { FilterChips, type ChipOption } from '@/components/domain';
import { catalog, reverse } from '@/api';
import { useAuthStore } from '@/store';
import type { Category, City, ReverseRequest } from '@/types/domain';
import { logger } from '@/utils/logger';
import styles from './pages.module.css';

const TOP_N = 8;

export function ReverseBoardPage(): JSX.Element {
  // F-170 B2.7: page renamed to "Шукаю майстра". Clients no longer see
  // the public list of other clients' requests — only their own
  // ("Мої запити") with status badges. Specialists keep the public list
  // (it's their inbox).
  const user = useAuthStore((s) => s.user);
  const isClient = user?.role === 'client' || (user && user.role !== 'specialist');
  const [items, setItems] = useState<ReverseRequest[] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [categories, setCategories] = useState<Category[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const categorySlug = searchParams.get('category') ?? '';
  const citySlug = searchParams.get('city') ?? '';

  // Load filter options once. We slice to TOP_N to keep the chip row compact;
  // power-search will land in F-160 D4 alongside the search page.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cats, cs] = await Promise.all([
          catalog.fetchCategories(),
          catalog.fetchCities(),
        ]);
        if (cancelled) return;
        setCategories((cats.results ?? []).slice(0, TOP_N));
        setCities((cs.results ?? []).slice(0, TOP_N));
      } catch (err) {
        logger.warn('Board filter options fetch failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Refetch the board whenever the URL filters change.
  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    (async () => {
      try {
        const data = await reverse.fetchReverseList({
          category: categorySlug || undefined,
          city: citySlug || undefined,
          // Clients see only their own requests (B2.7); specialists see
          // the whole inbox so they can pick what to respond to.
          mine: isClient ? true : undefined,
        });
        if (!cancelled) setItems(data.results);
      } catch (err) {
        logger.warn('Reverse list fetch failed', err);
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setItems(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [categorySlug, citySlug, isClient, reloadKey]);

  const categoryOptions: ChipOption[] = useMemo(
    () => categories.map((c) => ({ value: c.slug, label: c.name_ua })),
    [categories],
  );
  const cityOptions: ChipOption[] = useMemo(
    () => cities.map((c) => ({ value: c.slug, label: c.name_ua })),
    [cities],
  );

  const updateParam = (key: 'category' | 'city', value: string): void => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  return (
    <Layout
      title="Шукаю майстра"
      headerRight={
        <Link to="/board/new">
          <Button compact>+ Запит</Button>
        </Link>
      }
    >
      <p className={styles.note} style={{ marginBottom: 12 }}>
        Опиши потребу — бот знайде до 3 спеціалістів і вони відкриють
        чат із тобою. Не треба шукати самому.
      </p>
      {/* Specialists keep the public inbox + filters. Clients see only
          their own "Мої запити" (B2.7), so we suppress the discovery
          filters for them — there's nothing to discover. */}
      {!isClient ? (
        <div className={styles.boardFilters}>
          <FilterChips
            options={categoryOptions}
            selected={categorySlug ? [categorySlug] : []}
            onChange={(arr) => updateParam('category', arr[0] ?? '')}
            multi={false}
          />
          <FilterChips
            options={cityOptions}
            selected={citySlug ? [citySlug] : []}
            onChange={(arr) => updateParam('city', arr[0] ?? '')}
            multi={false}
          />
        </div>
      ) : (
        <h2 className={styles.sectionTitle}>Мої запити</h2>
      )}
      {error ? (
        <ErrorState onRetry={() => setReloadKey((k) => k + 1)} />
      ) : items === null ? (
        <Spinner center />
      ) : items.length === 0 ? (
        <EmptyState
          icon="orders"
          title={isClient ? 'Ще немає запитів' : 'Дошка тиха'}
          message={
            isClient
              ? 'Опиши потребу — і бот підкине її трьом спеціалістам.'
              : 'Опублікуй перший запит — спеціалісти відгукнуться.'
          }
          actions={
            <Link to="/board/new">
              <Button>Створити запит</Button>
            </Link>
          }
        />
      ) : (
        <div className={styles.list}>
          {items.map((r) => (
            <Link
              key={r.id}
              to={`/board/${r.id}`}
              style={{ display: 'block', textDecoration: 'none' }}
            >
              <Card interactive>
                <strong>
                  {r.category.icon} {r.category.name_ua} · {r.city.name_ua}
                </strong>
                <p className={styles.note}>
                  {isClient ? (
                    <Badge variant={r.status === 'live' ? 'accent' : 'muted'}>
                      {r.status === 'live'
                        ? 'Активний'
                        : r.status === 'closed'
                          ? 'Закритий'
                          : 'На модерації'}
                    </Badge>
                  ) : r.is_anonymous ? (
                    <Badge icon="user-x" variant="muted">
                      Анонімно
                    </Badge>
                  ) : (
                    r.client_first_name
                  )}
                </p>
                <p className={styles.note}>{r.description.slice(0, 140)}…</p>
                <p className={styles.note}>
                  {r.response_count} відповідей · {dayjs(r.created_at).format('D MMM')}
                </p>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </Layout>
  );
}
